import React, { useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';

/**
 * AI-powered ChatBot component for 24/7 visitor engagement
 * Author: VB Entreprise
 * 
 * Features:
 * - Floating chat widget
 * - Quick response options
 * - Lead qualification
 * - Smooth animations
 */
const ChatBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi! I'm here to help you learn more about VB Entreprise's services. How can I assist you today?",
      isBot: true,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');

  // Quick response options for common inquiries
  const quickResponses = [
    "Tell me about your services",
    "Request a consultation",
    "Pricing information",
    "Case studies"
  ];

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const newMessage = {
      id: messages.length + 1,
      text: inputValue,
      isBot: false,
      timestamp: new Date()
    };

    setMessages([...messages, newMessage]);
    setInputValue('');

    // Simulate bot response
    setTimeout(() => {
      const botResponse = {
        id: messages.length + 2,
        text: "Thank you for your message! I'll connect you with one of our specialists who will get back to you within 15 minutes. Would you like to schedule a free consultation call?",
        isBot: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
    }, 1000);
  };

  const handleQuickResponse = (response: string) => {
    const newMessage = {
      id: messages.length + 1,
      text: response,
      isBot: false,
      timestamp: new Date()
    };

    setMessages([...messages, newMessage]);

    // Simulate contextual bot response
    setTimeout(() => {
      let botText = "";
      switch (response) {
        case "Tell me about your services":
          botText = "We offer comprehensive IT and digital marketing services including web development, mobile apps, cloud solutions, SEO, and managed IT services. Which area interests you most?";
          break;
        case "Request a consultation":
          botText = "Great! I'd be happy to schedule a free consultation. Please provide your email and preferred time, and our team will reach out to you shortly.";
          break;
        case "Pricing information":
          botText = "Our pricing is based on project scope and specific requirements. We provide custom quotes after understanding your needs. Would you like to schedule a consultation to discuss your project?";
          break;
        case "Case studies":
          botText = "We have successful case studies across various industries including fintech, healthcare, and e-commerce. Check out our Case Studies page or I can send you specific examples. What industry are you in?";
          break;
        default:
          botText = "I'd be happy to help you with that! Can you provide more details about what you're looking for?";
      }

      const botResponse = {
        id: messages.length + 2,
        text: botText,
        isBot: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
    }, 1000);
  };

  return (
    <>
      {/* Chat Widget */}
      <div className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
        isOpen ? 'w-80 h-96' : 'w-auto h-auto'
      }`}>
        {isOpen ? (
          <div className="bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col h-full">
            {/* Header */}
            <div className="bg-blue-600 text-white p-4 rounded-t-lg flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-800 rounded-full flex items-center justify-center">
                  <MessageCircle size={16} />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">VB Assistant</h3>
                  <p className="text-xs text-blue-100">Online now</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.isBot ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`max-w-xs p-3 rounded-lg text-sm ${
                      message.isBot
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-blue-600 text-white'
                    }`}
                  >
                    {message.text}
                  </div>
                </div>
              ))}

              {/* Quick Responses */}
              {messages.length === 1 && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 text-center">Quick options:</p>
                  {quickResponses.map((response, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickResponse(response)}
                      className="w-full text-left p-2 text-sm bg-gray-50 hover:bg-gray-100 rounded border transition-colors"
                    >
                      {response}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Type your message..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <button
                  onClick={handleSendMessage}
                  className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsOpen(true)}
            className="bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300 hover:scale-110"
          >
            <MessageCircle size={24} />
          </button>
        )}
      </div>
    </>
  );
};

export default ChatBot;